import { useEffect, useRef, useState, type ReactNode } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Shared in-dropdown search behavior for the persona / product / narrative
// angle / offer pickers. The search box appears once a dropdown has this many
// items or more.
export const PICKER_SEARCH_THRESHOLD = 4;

export function usePickerSearch(open: boolean, optionCount: number, enabled = true) {
  const showSearch = enabled && optionCount >= PICKER_SEARCH_THRESHOLD;
  const [query, setQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) {
      setQuery("");
    } else if (showSearch) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open, showSearch]);
  const trimmed = query.trim().toLowerCase();
  const isSearching = showSearch && !!trimmed;
  const matchesLabel = (label: string) => label.toLowerCase().includes(trimmed);
  return { showSearch, query, setQuery, searchInputRef, isSearching, matchesLabel };
}

export function PickerSearchHeader({
  inputRef,
  query,
  onQueryChange,
  testId,
}: {
  inputRef: React.RefObject<HTMLInputElement>;
  query: string;
  onQueryChange: (v: string) => void;
  testId: string;
}) {
  return (
    <div
      className="sticky top-0 z-10 bg-popover px-2 py-1.5 border-b border-border/60"
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder="Search…"
          className="h-8 pl-7 text-xs"
          data-testid={testId}
        />
      </div>
    </div>
  );
}

export function PickerNoMatches() {
  return (
    <div className="px-3 py-6 text-center text-xs text-muted-foreground">
      No matches
    </div>
  );
}

export type SearchableSelectOption = {
  value: string;
  /** Plain-text label used for search matching and as the default display. */
  label: string;
  /** Optional custom display node (falls back to `label` when omitted). */
  node?: ReactNode;
  disabled?: boolean;
  testId?: string;
};

/**
 * A plain shadcn Select with built-in type-to-search (4+ item threshold),
 * composing usePickerSearch + PickerSearchHeader + PickerNoMatches. Use this
 * for any long, data-driven item picker so behavior stays consistent with the
 * Ads Express / LP Express / Creative Tinder pickers.
 */
export function SearchableSelect({
  value,
  onValueChange,
  disabled,
  placeholder,
  testId,
  triggerId,
  triggerClassName,
  triggerIcon,
  contentClassName,
  options,
  emptyContent,
  searchEnabled = true,
  alwaysShowSearch = false,
  selectedDisplay,
  ariaLabel,
}: {
  value: string;
  onValueChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
  testId?: string;
  triggerId?: string;
  triggerClassName?: string;
  triggerIcon?: ReactNode;
  contentClassName?: string;
  options: SearchableSelectOption[];
  emptyContent?: ReactNode;
  searchEnabled?: boolean;
  alwaysShowSearch?: boolean;
  selectedDisplay?: ReactNode;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const { showSearch, query, setQuery, searchInputRef, isSearching, matchesLabel } =
    usePickerSearch(open, alwaysShowSearch ? Math.max(options.length, PICKER_SEARCH_THRESHOLD) : options.length, searchEnabled);
  const filtered = isSearching ? options.filter((o) => matchesLabel(o.label)) : options;
  return (
    <Select
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      open={open}
      onOpenChange={setOpen}
    >
      <SelectTrigger
        id={triggerId}
        className={triggerClassName}
        data-testid={testId}
        aria-label={ariaLabel}
      >
        {triggerIcon}
        <SelectValue placeholder={placeholder}>{selectedDisplay}</SelectValue>
      </SelectTrigger>
      <SelectContent className={contentClassName}>
        {showSearch && (
          <PickerSearchHeader
            inputRef={searchInputRef}
            query={query}
            onQueryChange={setQuery}
            testId={testId ? `input-search-${testId}` : "input-search-picker"}
          />
        )}
        {showSearch && filtered.length === 0 && <PickerNoMatches />}
        {filtered.map((o) => (
          <SelectItem key={o.value} value={o.value} disabled={o.disabled} data-testid={o.testId}>
            {o.node ?? o.label}
          </SelectItem>
        ))}
        {options.length === 0 && emptyContent}
      </SelectContent>
    </Select>
  );
}
